// Stage Mail-V2 (2026-07-22 / Step 16): useFolderCounts — Sidebar 真实 counts。
//
// 设计:
//   - 拉一次 /api/mail-v2/folder-counts → 8 个固定 folder + total + unread_total
//   - 提供 refresh() 给 archive/trash/spam/batch 等动作完成后调用
//   - MAIL_V2_REAL=false 时 返回 demo shape(0)
//
// 8 个 folder:
//   inbox / sent / drafts / starred / important / spam / trash / archive
// (スヌーズ中 は削除 — 系统 Snooze 未実装)

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { mailV2Api } from "../../../api/mail-v2";

export type FolderCountKey =
  | "inbox"
  | "sent"
  | "drafts"
  | "starred"
  | "important"
  | "spam"
  | "trash"
  | "archive"
  | "total"
  | "unread_total";

export type FolderCounts = Record<FolderCountKey, { total: number; unread: number }>;

const EMPTY: FolderCounts = {
  inbox: { total: 0, unread: 0 },
  sent: { total: 0, unread: 0 },
  drafts: { total: 0, unread: 0 },
  starred: { total: 0, unread: 0 },
  important: { total: 0, unread: 0 },
  spam: { total: 0, unread: 0 },
  trash: { total: 0, unread: 0 },
  archive: { total: 0, unread: 0 },
  total: { total: 0, unread: 0 },
  unread_total: { total: 0, unread: 0 },
};

export interface UseFolderCountsOptions {
  enabled: boolean;
  mailAccountId?: string;
}

export function useFolderCounts(opts: UseFolderCountsOptions) {
  const { enabled, mailAccountId } = opts;
  const [counts, setCounts] = useState<FolderCounts>(EMPTY);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inFlightRef = useRef(false);
  const pendingRefreshRef = useRef(false);
  const [reloadSeq, setReloadSeq] = useState(0);

  // ★ 2026-07-22 真凶修复: 显式稳定 deps 数组 — 所有项都是非 undefined 原语。
  // React 18.3.1 areHookInputsEqual 在第二次 render 时如果 prevDeps[1] 是 undefined 会抛
  // 'Cannot read properties of undefined (reading length)'。
  // 用 useMemo 包装 deps 数组 + 显式归一化所有项为 string | null 或 boolean。
  const stableEnabled: boolean = Boolean(enabled);
  const stableMailAccountId: string | null =
    typeof mailAccountId === "string" && mailAccountId.length > 0 ? mailAccountId : null;

  // 预计算 deps 数组 — useMemo 保证引用稳定,deps 项全部是确定的非 undefined 值。
  const refreshDeps = useMemo<readonly [boolean, string | null]>(
    () => [stableEnabled, stableMailAccountId],
    [stableEnabled, stableMailAccountId],
  );

  const refresh = useCallback(async () => {
    setReloadSeq((prev) => prev + 1);
  }, []);

  const fetchCounts = useCallback(async () => {
    if (!stableEnabled) {
      setCounts(EMPTY);
      return;
    }
    if (inFlightRef.current) {
      pendingRefreshRef.current = true;
      return;
    }
    inFlightRef.current = true;
    setLoading(true);
    setError(null);
    try {
      const data = await mailV2Api.folderCounts(
        stableMailAccountId ? { mail_account_id: stableMailAccountId } : undefined,
      );
      // 后端返回 snake-or-camel?后端返回固定 key: inbox/sent/... 都是 lowercase
      const merged: FolderCounts = { ...EMPTY };
      for (const k of Object.keys(EMPTY) as FolderCountKey[]) {
        const v = (data as unknown as Record<string, { total?: number; unread?: number }>)?.[k];
        if (v) {
          merged[k] = {
            total: Number(v.total ?? 0),
            unread: Number(v.unread ?? 0),
          };
        }
      }
      setCounts(merged);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      inFlightRef.current = false;
      setLoading(false);
      if (pendingRefreshRef.current) {
        pendingRefreshRef.current = false;
        setReloadSeq((prev) => prev + 1);
      }
    }
  // ★ 2026-07-22 真凶修复: 用 useMemo 预计算的 refreshDeps (readonly tuple [boolean, string|null])
  // 代替内联 [stableEnabled, stableMailAccountId]。
  // 关键保证: deps 数组引用稳定 + 所有项类型确定为 non-undefined primitive。
  }, [refreshDeps]);

  useEffect(() => {
    void fetchCounts();
  }, [fetchCounts, reloadSeq]);

  return { counts, loading, error, refresh };
}
